/**
 * Variant Click Logger
 * Logs all variant selection clicks to help debug variant selection issues
 */

class VariantClickLogger {
  constructor() {
    this.logs = [];
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.attachListeners();
      });
    } else {
      this.attachListeners();
    }
  }

  attachListeners() {
    // Listen to all variant-related elements
    this.listenToVariantRadios();
    this.listenToVariantSelects();
    this.listenToVariantButtons();
    this.listenToQuickAddButtons();
    this.listenToFormSubmit();

    // Also listen to the variant change event from VariantSelects/VariantRadios
    document.addEventListener('change', (event) => {
      if (event.target.matches('variant-radios input[type="radio"], variant-selects select')) {
        // Wait a bit for the variant ID to be updated in the input
        setTimeout(() => {
          this.logVariantClick(event.target);
        }, 50);
      }
    });
  }

  listenToFormSubmit() {
    // Listen for form submit
    document.addEventListener('submit', (event) => {
      if (event.target.matches('form[action*="/cart/add"]')) {
        this.captureDefaultState(event.target);
      }
    });

    // Also listen for add to cart button clicks (for ajax carts)
    document.addEventListener('click', (event) => {
      const button = event.target.closest('button[name="add"], .product-form__submit');
      if (button) {
        const form = button.closest('form');
        if (form) {
          this.captureDefaultState(form);
        }
      }
    });
  }

  captureDefaultState(form) {
    // Only capture default state if logs are empty (no user interaction yet)
    if (this.logs.length > 0) return;

    const variantInput = form.querySelector('input[name="id"]');
    const variantId = variantInput ? variantInput.value : null;

    if (!variantId) return;

    // Find all checked inputs within this form or associated with this product
    // We look for inputs that are checked but haven't been logged
    const section = form.closest('.shopify-section') || document;
    
    // Find checking radio buttons associated with this form
    const checkedRadios = section.querySelectorAll('variant-radios input[type="radio"]:checked, fieldset input[type="radio"]:checked');
    const selectedSelects = section.querySelectorAll('variant-selects select, .product-form__input select');

    let captureHappened = false;

    // Capture Radios
    checkedRadios.forEach(radio => {
       const label = this.getCleanText(radio);
       if (label) {
         this.logDefaultSelection(label, variantId);
         captureHappened = true;
       }
    });

    // Capture Selects
    selectedSelects.forEach(select => {
      const label = this.getCleanText(select);
      if (label) {
        this.logDefaultSelection(label, variantId);
        captureHappened = true;
      }
    });

    // If we couldn't find specific inputs but have a variant ID, log a generic default
    if (!captureHappened) {
       // Try to find the variant title from the variant object if available in window.productVariants or similar
       // Otherwise just log "Default Variant"
       this.logDefaultSelection("Default Selection", variantId);
    }
  }

  logDefaultSelection(label, variantId) {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const url = window.location.pathname;

    const logEntry = {
      label: label,
      url: url,
      button: 'default-selection',
      is_mobile: isMobile
    };

    this.logs.push(logEntry);
    this.updateLogInput();
    console.log('[Variant Click Logger] Captured default state:', logEntry);
  }

  listenToQuickAddButtons() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('.quick-add__sizes');
      if (button) {
        this.handleQuickAddClick(button);
      }
    });
  }

  handleQuickAddClick(button) {
    const form = button.closest('form');
    let variantId = null;
    if (form) {
      const input = form.querySelector('input[name="id"]');
      if (input) variantId = input.value;
    }
    this.logVariantClick(button, null, variantId);
  }

  listenToVariantRadios() {
    // Listen to all radio inputs for variants
    const radioInputs = document.querySelectorAll(
      'variant-radios input[type="radio"], fieldset input[type="radio"][name*="option"], input[type="radio"][form*="product-form"]'
    );

    radioInputs.forEach((input) => {
      input.addEventListener('click', (event) => {
        // Wait a bit for the variant ID to be updated in the input
        setTimeout(() => {
          this.logVariantClick(event.target);
        }, 50);
      });
    });
  }

  listenToVariantSelects() {
    // Listen to all select dropdowns for variants
    const selects = document.querySelectorAll('variant-selects select, select[name*="option"]');

    selects.forEach((select) => {
      select.addEventListener('change', (event) => {
        // Wait a bit for the variant ID to be updated in the input
        setTimeout(() => {
          this.logVariantClick(event.target);
        }, 50);
      });
    });
  }

  listenToVariantButtons() {
    // Listen to variant picker buttons (if using button-style variant picker)
    const variantButtons = document.querySelectorAll(
      '[data-variant-id], .variant-picker button, .product-form__input label'
    );

    variantButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        // Find associated input
        const input =
          event.target.control ||
          document.getElementById(event.target.getAttribute('for')) ||
          event.target.querySelector('input');

        if (input) {
          this.logVariantClick(input, event.target);
        }
      });
    });
  }

  logVariantClick(element, clickedElement = null, explicitVariantId = null) {
    const timestamp = new Date().toISOString();
    const value = element.value || '';
    const name = element.name || 'unknown';

    // Prevent duplicate logging for the same element within 50ms
    // We use a separate property to track this since we don't store timestamp in the logs anymore
    if (
      this.lastClickContext &&
      this.lastClickContext.value === value &&
      this.lastClickContext.name === name &&
      new Date(timestamp) - new Date(this.lastClickContext.timestamp) < 50
    ) {
      return;
    }

    // Update last click context
    this.lastClickContext = {
      value: value,
      name: name,
      timestamp: timestamp
    };

    const label = clickedElement ? this.getCleanText(clickedElement) : this.getCleanText(element);
    
    // Determine context
    const buttonContext = this.getButtonContext(element);
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const url = window.location.pathname;

    const logEntry = {
      label: label,
      url: url,
      button: buttonContext,
      is_mobile: isMobile
    };

    this.logs.push(logEntry);
    this.trimLogsToFit();
    this.updateLogInput();
    
    // Also log to console for debugging
    console.log('[Variant Click Logger]', logEntry);
  }

  getButtonContext(element) {
    // Check for Quick Add
    if (element.closest('.quick-add-modal') || element.closest('quick-add-modal') || element.closest('.quick-add__sizes')) {
      return 'quick-add';
    }
    
    // Check for Cart Drawer
    if (element.closest('.drawer') || element.closest('cart-drawer')) {
      return 'cart-drawer';
    }
    
    // Check for Main Product section
    if (element.closest('.product__info-container') || element.closest('.product-section')) {
      return 'product-page';
    }

    // Default to checking if it's a form inside a product section
    if (element.closest('form[action*="/cart/add"]')) {
      return 'add-to-cart-form';
    }

    return 'unknown';
  }

  trimLogsToFit() {
    const MAX_LENGTH = 1024;
    let jsonString = JSON.stringify(this.logs);

    // If we're under the limit, we're good
    if (jsonString.length <= MAX_LENGTH) {
      return;
    }

    // Keep removing the oldest entries until we fit
    // Always keep at least the last entry (most important)
    while (this.logs.length > 1 && jsonString.length > MAX_LENGTH) {
      this.logs.shift(); // Remove the oldest entry
      jsonString = JSON.stringify(this.logs);
    }

    // Log a warning if we had to trim
    if (this.logs.length === 1 && jsonString.length > MAX_LENGTH) {
      console.warn('[Variant Click Logger] Single log entry exceeds 1024 characters!');
    }
  }

  getOptionName(element) {
    // Try to find the option name from fieldset legend or label
    if (element.closest && typeof element.closest === 'function') {
      const fieldset = element.closest('fieldset');
      if (fieldset) {
        const legend = fieldset.querySelector('legend');
        if (legend) {
          return legend.textContent.trim();
        }
      }
    }

    // Check if it is a quick add size button
    if (element.classList && element.classList.contains('quick-add__sizes')) {
      return 'Size';
    }

    // Fallback to name attribute
    return element.name || 'unknown';
  }

  getCurrentVariantId() {
    // Try to get the current variant ID from the hidden input
    // Priority: .product-variant-id with name="id", then any input[name="id"][type="hidden"]
    const variantInput =
      document.querySelector('.product-variant-id[name="id"], .product-variant-id') ||
      document.querySelector('input[name="id"][type="hidden"]');
    return variantInput ? variantInput.value : null;
  }

  getCleanText(element) {
    let textElement = element;

    // If it's an input, find its label
    if (element.tagName === 'INPUT' && element.id) {
      const label = document.querySelector(`label[for="${element.id}"]`);
      if (label) {
        textElement = label;
      }
    }

    if (textElement.tagName === 'LABEL') {
      // Clone the element to avoid modifying the DOM
      const clone = textElement.cloneNode(true);
      // Remove all visually-hidden elements
      const hiddenElements = clone.querySelectorAll('.visually-hidden, .sr-only');
      hiddenElements.forEach((el) => el.remove());
      return clone.textContent.trim();
    }

    if (element.tagName === 'BUTTON') {
      const sizeValue = element.querySelector('.size-value');
      if (sizeValue) {
        return sizeValue.textContent.trim();
      }
      return element.textContent.trim();
    }

    if (element.tagName === 'SELECT') {
      const selectedOption = element.options[element.selectedIndex];
      return selectedOption ? selectedOption.textContent.trim() : element.value;
    }

    return element.value || '';
  }

  getVariantAvailability() {
    // Try to get availability from the add to cart button
    const addToCartButton = document.querySelector('.product-form__submit, button[name="add"]');
    if (addToCartButton) {
      return !addToCartButton.disabled;
    }
    return null;
  }

  updateLogInput() {
    // Find all log inputs (there might be multiple forms on the page)
    const logInputs = document.querySelectorAll('.product-variant-log, input[name="_log"]');

    logInputs.forEach((input) => {
      input.value = JSON.stringify(this.logs);
    });
  }

  // Public method to get logs
  getLogs() {
    return this.logs;
  }

  // Public method to clear logs
  clearLogs() {
    this.logs = [];
    this.updateLogInput();
  }
}

// Initialize the logger
if (typeof window !== 'undefined') {
  window.variantClickLogger = new VariantClickLogger();
}
